/**
 * 顶部菜单 / §4.2 默认路由共用的 RBAC 菜单码（与后端 type=1 `menuCodes` 字符串一致）。
 * 联调时请与后端 RBAC 配置对齐后仅改此处。
 *
 * 无菜单权限被重定向到 `/dashboard` 或从 `/`、`/client` 再进入业务区时，`App.js` / `ClientDefaultRedirect` 会对已登录用户拉取 RBAC；**整页刷新（F5）**会经 store 重置后再拉一次（与 token 定时刷新、切标签解耦）。
 *
 * @see docs/RBAC按类型权限码接口与集成设计.md §4.2.1
 */

/** 「首页」——菜单可进大屏/指挥中心；默认落地 `/client/visualScreen` */
export const HOME = 'home'

/**
 * 订单域粗码。订单列表页本身认 menus.js 的 `order:list`，
 * 只有粗码 `order` 时不能把默认落地指到订单列表，否则守卫会把同一地址再跳回来。
 */
export const ORDER = 'order'

/**
 * 派单侧菜单码（与 RBAC 下发一致）：具备时登录进入系统默认进订单·派单页。
 * 与 `menus.js` 中 `order:dispatcher` 二选一或并存均可。
 */
export const DISPATCHER_ORDER = 'order:dispatcher:order'

/** 「系统管理」——对应默认子页 `/client/systemControl/agents` */
export const SYSTEM = 'system'

/** 「监控大屏」父级菜单码 */
export const SUR = 'sur'

/** 「婴儿室监控」——对应 `/client/surveillance/list`（moduleCodes：`sur:babyroom:list`） */
export const SUR_BABYROOM_LIST = 'sur:babyroom:list'

/** 「婴儿室监控 · 切换店铺」（buttonCodes：`sur:babyroom:store`） */
export const SUR_BABYROOM_STORE = 'sur:babyroom:store'

/** 「按摩 · 日看板」——对应 `/client/tuina/board` */
export const TUINA_BOARD = 'tuina:board'

/** 「按摩 · 预约列表」——对应 `/client/tuina/appointments` */
export const TUINA_APPOINTMENTS = 'tuina:appointments'

/** 「按摩 · 订单列表」——对应 `/client/tuina/orders` */
export const TUINA_ORDERS = 'tuina:orders'

/** 「按摩 · 服务项目」——对应 `/client/tuina/projects` */
export const TUINA_PROJECTS = 'tuina:projects'

/** 「按摩 · 师傅」——对应 `/client/tuina/technicians` */
export const TUINA_TECHNICIANS = 'tuina:technicians'

/** 「按摩 · 排班」——对应 `/client/tuina/roster` */
export const TUINA_ROSTER = 'tuina:roster'

/** 「按摩 · 会员」——对应 `/client/tuina/members` */
export const TUINA_MEMBERS = 'tuina:members'

/**
 * 合并 RBAC 按类型下发的菜单/模块/按钮码。同一业务码可能落在 `menuCodes` 或 `moduleCodes`（甚至 `buttonCodes`），
 * 仅看 `menuCodes` 会导致默认路由、路由守卫、侧栏与后端配置不一致（例如 `order:dispatcher:order` 只在 module 侧时「无权限/一直转圈」前表现为无法进页）。
 *
 * @param {string[]|{ menuCodes?: string[], moduleCodes?: string[], buttonCodes?: string[] }|null|undefined} input
 * @returns {string[]}
 */
export function mergeClientPermissionCodeLists (input) {
  if (Array.isArray(input)) return input
  if (input == null || typeof input !== 'object') return []
  return [
    ...(Array.isArray(input.menuCodes) ? input.menuCodes : []),
    ...(Array.isArray(input.moduleCodes) ? input.moduleCodes : []),
    ...(Array.isArray(input.buttonCodes) ? input.buttonCodes : [])
  ]
}

/**
 * §4.2.1：按权限码（菜单+模块+按钮并集）优先级解析 `/client` 默认子路径。
 * 亦用于 `ClientOutletGuard`：无 URL 访问权限时的 `replace` 落点，与访问 `/client` 根路径的默认跳转一致。
 *
 * @param {string[]|{ menuCodes?: string[], moduleCodes?: string[], buttonCodes?: string[] }|undefined} menuCodesOrPayload - 传数组时视为仅 `menuCodes`（兼容旧调用）
 * @returns {string}
 */
export function resolveClientDefaultPath (menuCodesOrPayload) {
  const codes = mergeClientPermissionCodeLists(menuCodesOrPayload)

  if (codes.includes(HOME)) {
    return '/client/visualScreen'
  }
  if (codes.includes('order:list')) {
    return '/client/order/orderList'
  }
  if (codes.includes(DISPATCHER_ORDER) || codes.includes('order:dispatcher')) {
    return '/client/order/dispatch'
  }
  if (codes.includes(SYSTEM)) {
    return '/client/systemControl/agents'
  }
  if (codes.includes(SUR_BABYROOM_LIST)) {
    return '/client/surveillance/list'
  }
  if (codes.includes(TUINA_BOARD)) {
    return '/client/tuina/board'
  }
  if (codes.includes(TUINA_APPOINTMENTS)) {
    return '/client/tuina/appointments'
  }
  if (codes.includes(TUINA_ORDERS)) {
    return '/client/tuina/orders'
  }
  if (codes.includes(TUINA_PROJECTS)) {
    return '/client/tuina/projects'
  }
  if (codes.includes(TUINA_TECHNICIANS)) {
    return '/client/tuina/technicians'
  }
  if (codes.includes(TUINA_ROSTER)) {
    return '/client/tuina/roster'
  }
  if (codes.includes(TUINA_MEMBERS)) {
    return '/client/tuina/members'
  }
  return '/dashboard'
}
